
import { useState, useEffect } from "react";
import { VoteButton } from "@/components/VoteButton";
import { PrizeDialog } from "@/components/PrizeDialog";
import type { Prize } from "@/types";
import { getActiveUniversity } from "@/utils/prize/universityUtils";
import { toast } from "sonner";

interface TipCardProps {
  tip: string;
  title: string;
  description?: string;
  votes?: number; // Keep this for backward compatibility
  tipId: number;
  tipNumber?: number;
  emoji: string;
  onVote: () => void;
  unsw_votes: number;
  usyd_votes: number;
  uoa_votes: number;
}

export function TipCard({ 
  tip, 
  title,
  description,
  tipId,
  tipNumber,
  emoji,
  onVote,
  unsw_votes = 0,
  usyd_votes = 0,
  uoa_votes = 0
}: TipCardProps) {
  const [showPrizeDialog, setShowPrizeDialog] = useState(false);
  const [prize, setPrize] = useState<Prize | null>(null);
  const [activeUniversityVotes, setActiveUniversityVotes] = useState(unsw_votes); // Default to UNSW votes

  useEffect(() => {
    let isMounted = true;

    const fetchActiveUniversityVotes = async () => {
      try {
        const activeUniversity = await getActiveUniversity();
        
        if (!isMounted) return;

        const universityName = activeUniversity.name.toUpperCase();
        let votes = unsw_votes; // Default to UNSW votes

        switch (universityName) {
          case 'UNSW':
            votes = unsw_votes;
            break;
          case 'USYD':
            votes = usyd_votes;
            break;
          case 'UOA':
            votes = uoa_votes;
            break;
          default:
            console.warn('Unknown university:', universityName);
        }
        
        if (isMounted) {
          setActiveUniversityVotes(votes);
        }
      } catch (error) {
        console.error('Error fetching active university:', error);
        if (isMounted) {
          toast.error('Error loading votes. Using default university.');
          setActiveUniversityVotes(unsw_votes);
        }
      }
    };

    fetchActiveUniversityVotes();

    return () => {
      isMounted = false;
    };
  }, [unsw_votes, usyd_votes, uoa_votes]);

  const handleVote = () => {
    console.log('Vote registered for tip:', tipId);
    onVote();
  };

  const handlePrizeWon = (wonPrize: Prize) => {
    console.log('Prize won:', wonPrize);
    setPrize(wonPrize);
    setShowPrizeDialog(true);
  };

  const handlePrizeClaim = () => {
    console.log('Prize claimed, closing dialog');
    setShowPrizeDialog(false);
  };

  return (
    <div className="relative group transform transition-all duration-300 hover:scale-[1.02]">
      <div className="flex justify-start w-full absolute left-0 top-0 animate-float z-20" style={{ left: "-2%", top: "-10%" }}>
        <div className="flex items-center bg-[#19b4e8] px-2 py-2 rounded-2xl shadow-lg w-auto">
          <img src="/imgs/heart.png" alt="heart" className="w-auto h-[35px] mr-3 animate-heartbeat" id="heart" />
          <span className="text-3xl font-bold text-white font-Metric mr-2"
            style={{
              textShadow: '3px 3px 2px #0a93c0'
            }}>
            {activeUniversityVotes}
          </span>
        </div>
      </div>

      <div className="relative p-[2px]">
        <div
          className="p-[8px] rounded-[2.5em] shadow-[0_4px_10px_#005aaf] hover:shadow-[0px_12px_12px_#005aaf] transition-all duration-300"
          style={{
            background: 'linear-gradient(180deg, #1ab1e0, #0b74bc)',
          }}
        >
          <div className="bg-[#01144b] rounded-[2em] p-2">
            <div className="bg-red rounded-[1.5em] p-6" style={{
              background: 'linear-gradient(10deg, rgba(2, 30, 113, 0.8), rgba(2, 30, 113, 0))'
            }}>
              <div className="absolute bottom-6 ml-[-18px]">
                <img src="/imgs/wing.png" alt="wing" className="w-auto h-[42px]" id="wing" />
              </div>
              <div className="relative">
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                  <div className="flex flex-col gap-4 text-left font-Quicksand">
                    <div className="space-y-2">
                      <h3 className="text-3xl font-bold text-vaseline-white">{title}</h3>
                    </div>
                    <div className="flex justify-between items-center gap-4">
                      <div className="flex-1">
                        <p className="text-2xl font-medium leading-relaxed text-vaseline-white/90">{tip}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <VoteButton 
                      votes={activeUniversityVotes} 
                      tipId={tipId} 
                      emoji={emoji} 
                      onVote={handleVote} 
                      onPrizeWon={handlePrizeWon} 
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PrizeDialog
        open={showPrizeDialog}
        onOpenChange={setShowPrizeDialog}
        prize={prize}
        onClaim={handlePrizeClaim}
      />
    </div>
  );
}
